import { useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { formatDate, titleCase } from '../../lib/format'
import type { HazardReport, IncidentReport } from '../../types'
import {
  Alert, Button, Card, CardBody, CardHeader, EmptyState, Input, Select,
  Skeleton, StatusBadge, Textarea,
} from '../../components/ui'
import { Wizard } from '../../components/Wizard'
import { AlertIcon, CloseIcon, PlusIcon, ShieldIcon } from '../../components/icons'

type Tab = 'incidents' | 'hazards'

interface IncidentsResponse {
  ok: boolean; items: IncidentReport[]; types?: string[]; severities?: string[]
}
interface HazardsResponse {
  ok: boolean; items: HazardReport[]; types?: string[]; risks?: string[]
}

/* Enum lists come from the API so the options always match what the backend
 * will accept; these only cover the case where an older build omits them. */
const FALLBACK_INCIDENT_TYPES = ['rockfall', 'equipment_failure', 'flooding', 'fire', 'vehicle', 'other']
const FALLBACK_SEVERITIES = ['minor', 'moderate', 'serious', 'fatal']
const FALLBACK_HAZARD_TYPES = ['unstable_ground', 'gas', 'electrical', 'noise', 'dust', 'other']
const FALLBACK_RISKS = ['low', 'medium', 'high', 'critical']

export default function Safety() {
  const [tab, setTab] = useState<Tab>('incidents')
  const [showForm, setShowForm] = useState(false)

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Safety</h1>
          <p className="page-head__sub">
            Report incidents that happened and hazards that could cause one.
          </p>
        </div>
        <Button className="fab-trigger__inline" icon={<PlusIcon />} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : tab === 'incidents' ? 'Report incident' : 'Report hazard'}
        </Button>
        <button
          type="button" className="fab"
          onClick={() => setShowForm((s) => !s)}
          aria-label={showForm ? 'Cancel' : tab === 'incidents' ? 'Report incident' : 'Report hazard'}
        >
          {showForm ? <CloseIcon size={22} /> : <PlusIcon size={22} />}
        </button>
      </div>

      <div className="tab-scroll" style={{ marginBottom: 'var(--sp-5)' }}>
        <Button variant={tab === 'incidents' ? 'primary' : 'secondary'} size="sm"
                onClick={() => { setTab('incidents'); setShowForm(false) }}>
          Incidents
        </Button>
        <Button variant={tab === 'hazards' ? 'primary' : 'secondary'} size="sm"
                onClick={() => { setTab('hazards'); setShowForm(false) }}>
          Hazards
        </Button>
      </div>

      {tab === 'incidents'
        ? <Incidents showForm={showForm} onDone={() => setShowForm(false)} />
        : <Hazards showForm={showForm} onDone={() => setShowForm(false)} />}
    </>
  )
}

function Incidents({ showForm, onDone }: { showForm: boolean; onDone: () => void }) {
  const qc = useQueryClient()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['miner', 'incidents'],
    queryFn: () => api.get<IncidentsResponse>('/miner/incidents'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.offline.post('/miner/incidents', body, 'Safety incident'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['miner', 'incidents'] }); onDone() },
  })

  const types = data?.types?.length ? data.types : FALLBACK_INCIDENT_TYPES
  const severities = data?.severities?.length ? data.severities : FALLBACK_SEVERITIES
  const items = data?.items ?? []

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    create.mutate({
      incident_type: f.get('incident_type'),
      severity: f.get('severity'),
      occurred_at: f.get('occurred_at'),
      description: f.get('description'),
      injuries_count: Number(f.get('injuries_count') || 0),
      fatalities_count: Number(f.get('fatalities_count') || 0),
      immediate_action: f.get('immediate_action') || '',
    })
  }

  return (
    <>
      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Report an incident"
                      sub="Something that already happened. Serious incidents alert your leader immediately." />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack">
                {create.isError && <Alert tone="danger">{(create.error as Error).message}</Alert>}
                {/* PWA: 3 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'What & when', content: (
                      <div className="grid grid--2">
                        <Select label="Incident type" name="incident_type" required defaultValue="">
                          <option value="" disabled>Select…</option>
                          {types.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
                        </Select>
                        <Select label="Severity" name="severity" required defaultValue="">
                          <option value="" disabled>Select…</option>
                          {severities.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
                        </Select>
                        <Input label="When did it happen?" name="occurred_at" type="datetime-local" required />
                      </div>
                    ) },
                    { title: 'People affected', content: (
                      <div className="grid grid--2">
                        <Input label="People injured" name="injuries_count" type="number" min="0" defaultValue={0} />
                        <Input label="Fatalities" name="fatalities_count" type="number" min="0" defaultValue={0} />
                      </div>
                    ) },
                    { title: 'Describe it', content: (
                      <div className="stack">
                        <Textarea label="What happened?" name="description" required
                                  placeholder="Describe the incident as clearly as you can." />
                        <Textarea label="Immediate action taken" name="immediate_action"
                                  placeholder="What was done right away, if anything." />
                      </div>
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={create.isPending} confirm={{ title: "Submit this report?", message: "It goes to your leadership for review." }}>Submit report</Button>
                      <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Your incident reports" />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} w="70%" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<AlertIcon size={22} />} title="No incidents reported"
                        body="That's good news. If something does happen, report it here." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Type</th><th>Severity</th><th>Occurred</th><th>Description</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {items.map((r, i) => (
                    <tr key={r.id ?? i}>
                      <td>{titleCase(r.incident_type)}</td>
                      <td><StatusBadge status={r.severity} /></td>
                      <td>{formatDate(r.occurred_at)}</td>
                      <td style={{ maxWidth: 320 }}>{r.description || '—'}</td>
                      <td><StatusBadge status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}

function Hazards({ showForm, onDone }: { showForm: boolean; onDone: () => void }) {
  const qc = useQueryClient()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['miner', 'hazards'],
    queryFn: () => api.get<HazardsResponse>('/miner/hazards'),
  })

  const create = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.offline.post('/miner/hazards', body, 'Hazard report'),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['miner', 'hazards'] }); onDone() },
  })

  const types = data?.types?.length ? data.types : FALLBACK_HAZARD_TYPES
  const risks = data?.risks?.length ? data.risks : FALLBACK_RISKS
  const items = data?.items ?? []

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    create.mutate({
      hazard_type: f.get('hazard_type'),
      risk_level: f.get('risk_level'),
      description: f.get('description'),
      potential_consequences: f.get('potential_consequences') || '',
      recommended_action: f.get('recommended_action') || '',
    })
  }

  return (
    <>
      {showForm && (
        <Card style={{ marginBottom: 'var(--sp-6)' }}>
          <CardHeader title="Report a hazard"
                      sub="Something that could cause harm if it isn't dealt with." />
          <CardBody>
            <form onSubmit={onSubmit}>
              <div className="stack">
                {create.isError && <Alert tone="danger">{(create.error as Error).message}</Alert>}
                {/* PWA: 2 guided steps; desktop stacked, unchanged. */}
                <Wizard
                  steps={[
                    { title: 'Hazard & risk', content: (
                      <div className="grid grid--2">
                        <Select label="Hazard type" name="hazard_type" required defaultValue="">
                          <option value="" disabled>Select…</option>
                          {types.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
                        </Select>
                        <Select label="Risk level" name="risk_level" required defaultValue="">
                          <option value="" disabled>Select…</option>
                          {risks.map((r) => <option key={r} value={r}>{titleCase(r)}</option>)}
                        </Select>
                      </div>
                    ) },
                    { title: 'Describe it', content: (
                      <div className="stack">
                        <Textarea label="Describe the hazard" name="description" required
                                  placeholder="Where is it, and what makes it dangerous?" />
                        <Textarea label="What could go wrong?" name="potential_consequences"
                                  placeholder="The harm this could cause if nothing changes." />
                        <Textarea label="Recommended action" name="recommended_action"
                                  placeholder="What you think should be done about it." />
                      </div>
                    ) },
                  ]}
                  footer={(
                    <div className="row">
                      <Button type="submit" loading={create.isPending} confirm={{ title: "Submit this report?", message: "It goes to your leadership for review." }}>Submit report</Button>
                      <Button type="button" variant="ghost" onClick={onDone}>Cancel</Button>
                    </div>
                  )}
                />
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader title="Your hazard reports" />
        <CardBody flush>
          {isError ? (
            <div style={{ padding: 'var(--sp-6)' }}><Alert tone="danger">{(error as Error).message}</Alert></div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--sp-6)' }} className="stack"><Skeleton h={20} /><Skeleton h={20} w="70%" /></div>
          ) : items.length === 0 ? (
            <EmptyState icon={<ShieldIcon size={22} />} title="No hazards reported"
                        body="Spotting a hazard early is the cheapest way to prevent an incident." />
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr><th>Type</th><th>Risk</th><th>Description</th><th>Reported</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {items.map((r, i) => (
                    <tr key={r.id ?? i}>
                      <td>{titleCase(r.hazard_type)}</td>
                      <td><StatusBadge status={r.risk_level} /></td>
                      <td style={{ maxWidth: 320 }}>{r.description || '—'}</td>
                      <td>{formatDate(r.occurred_at)}</td>
                      <td><StatusBadge status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </>
  )
}
